// MARK://dhk8/pos/stores/cashcut-store
// Cash-cut state — thin layer over lib/clients/cashcuts.ts (dhk8-pos-cashcuts).
//
// A cash cut is an immutable record with a `kind` (OPEN | X_REPORT | CLOSE | Z_REPORT)
// scoped to a shift. There is no open/close state machine on the cut itself — the
// "shift" is the grouping, identificado por `shiftId` que va EN CADA cut persistido
// (pos-cashcuts, server-side RLS = fuente de verdad).
//
// sessionStorage guarda el shiftId solo como CACHÉ rápida. Si se borra (cerrar
// pestaña / matar la PWA / dormir la tablet), `fetchCuts` lo REHIDRATA desde el
// backend: reconstruye el turno abierto a partir de los cuts y recupera su shiftId
// real (fix TCK-0012 — antes fabricaba un shiftId nuevo y fragmentaba el corte).
// TODO(reconcile): cuando dhk8-pos-sessions exponga PosShift en el graph, mover el
// estado de turno ahí; este store ya queda backend-driven mientras tanto.
//
// tenantId/branchId/cashierId come from the session.

import { create } from 'zustand'

import {
  listCashCuts, createCashCut, type CashCutDto, type CashCutKind, type Denomination,
} from '../lib/clients/cashcuts.js'
import { getTenantId } from '../lib/api.js'
import { useAuthStore } from './auth-store.js'

export type { CashCutDto, CashCutKind, Denomination } from '../lib/clients/cashcuts.js'

const SHIFT_KEY = 'dhk8_pos_shift'

function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getShiftId(): string | null {
  try { return sessionStorage.getItem(SHIFT_KEY) } catch { return null }
}
function setShiftId(id: string | null): void {
  try { if (id) sessionStorage.setItem(SHIFT_KEY, id); else sessionStorage.removeItem(SHIFT_KEY) } catch { /* ignore */ }
}

function ctx(): { tenantId: string; branchId: string; cashierId: string } {
  const user = useAuthStore.getState().user
  const tenantId = getTenantId()
  if (!tenantId || !user) throw new Error('Sesión no inicializada')
  return { tenantId, branchId: user.branchId, cashierId: user.id }
}

/**
 * Reconstruye el turno abierto desde los cuts persistidos (fuente de verdad).
 * Devuelve si hay turno abierto + su `shiftId` real + su cut OPEN (el fondo).
 * Un turno está abierto si su último OPEN no tiene un CLOSE/Z_REPORT posterior.
 */
function resolveOpenShift(cuts: CashCutDto[]): { open: boolean; shiftId: string | null; openCut: CashCutDto | null } {
  const sorted = [...cuts].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  let open = false
  let shiftId: string | null = null
  let openCut: CashCutDto | null = null
  for (const c of sorted) {
    if (c.kind === 'OPEN') { open = true; shiftId = c.shiftId; openCut = c }
    else if (c.kind === 'CLOSE' || c.kind === 'Z_REPORT') { open = false; shiftId = null; openCut = null }
  }
  return { open, shiftId, openCut }
}

export interface RecordCutInput {
  kind: CashCutKind
  /** Drawer counting; countedCents is derived as Σ(denomination*count) when given. */
  denominations?: Denomination[]
  /** Counted total in cents — used if `denominations` not given. */
  countedCents?: number
  /** System-expected cash; defaults to the opening fondo for closing/X cuts, 0 for OPEN. */
  expectedCents?: number
  notes?: string
  /** Lane petty-cash: tipifica el movimiento (PETTY_CASH_PURCHASE | PETTY_CASH_CHANGE | ARQUEO | …). */
  reason?: string
  /** Lane petty-cash: correlador (UUID) que liga DROP ↔ PettyCashPurchase ↔ PICKUP. */
  purchaseRef?: string
}

interface CashcutState {
  shiftId: string | null
  cuts: CashCutDto[]
  loading: boolean
  error: string | null
  /** Derived: shift has an OPEN cut and no CLOSE/Z_REPORT after it. */
  shiftOpen: boolean
  /** The OPEN cut for the current shift (the fondo declaration), if any. */
  openCut: CashCutDto | null
  fetchCuts: () => Promise<void>
  recordCut: (input: RecordCutInput) => Promise<CashCutDto>
}

export const useCashcutStore = create<CashcutState>((set, get) => ({
  shiftId: getShiftId(),
  cuts: [],
  loading: false,
  error: null,
  shiftOpen: false,
  openCut: null,

  async fetchCuts() {
    set({ loading: true, error: null })
    try {
      const { tenantId, branchId } = ctx()
      let sid = getShiftId()
      let res = await listCashCuts({ tenantId, branchId, ...(sid ? { shiftId: sid } : {}), limit: 50 })
      let cuts = res.items
      // Recuperación: si no hay shiftId local (sessionStorage borrado / PWA
      // relanzada) pero el backend muestra un turno abierto, rehidrata su shiftId
      // real y re-acota la lista a ese turno (evita fabricar uno nuevo → corte
      // fragmentado). Los cuts son la fuente de verdad (RLS server-side).
      if (!sid) {
        const recovered = resolveOpenShift(cuts)
        if (recovered.open && recovered.shiftId) {
          sid = recovered.shiftId
          setShiftId(sid)
          res = await listCashCuts({ tenantId, branchId, shiftId: sid, limit: 50 })
          cuts = res.items
        }
      }
      const resolved = resolveOpenShift(cuts)
      set({
        cuts,
        shiftId: sid,
        shiftOpen: resolved.open,
        openCut: resolved.openCut,
        loading: false,
      })
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'No se pudo cargar cortes' })
    }
  },

  async recordCut(input) {
    set({ loading: true, error: null })
    try {
      const { tenantId, branchId, cashierId } = ctx()
      // OPEN abre un turno nuevo; lo demás se adjunta al actual. Si no hay shiftId
      // local pero el backend tiene un turno abierto, recupéralo en lugar de
      // fabricar uno nuevo (evita fragmentar el corte — fix TCK-0012).
      let sid = getShiftId()
      if (input.kind === 'OPEN') {
        sid = uuid(); setShiftId(sid)
      } else if (!sid) {
        sid = resolveOpenShift(get().cuts).shiftId ?? uuid()
        setShiftId(sid)
      }

      const denoms = input.denominations?.filter((d) => d.count > 0) ?? []
      const countedCents = input.countedCents ?? denoms.reduce((a, d) => a + d.denomination * d.count, 0)
      const openFondo = get().openCut?.countedCents ?? 0
      const expectedCents = input.expectedCents ?? (input.kind === 'OPEN' ? 0 : openFondo)

      const created = await createCashCut({
        tenantId, branchId, shiftId: sid, cashierId,
        kind: input.kind,
        expectedCents,
        countedCents,
        ...(denoms.length ? { denominations: denoms } : {}),
        ...(input.notes ? { notes: input.notes } : {}),
        ...(input.reason ? { reason: input.reason } : {}),
        ...(input.purchaseRef ? { purchaseRef: input.purchaseRef } : {}),
      })

      // Closing the shift clears the logical shift id.
      if (input.kind === 'CLOSE' || input.kind === 'Z_REPORT') setShiftId(null)

      set((s) => {
        const cuts = [created, ...s.cuts]
        const resolved = resolveOpenShift(cuts)
        return {
          cuts,
          shiftId: getShiftId(),
          shiftOpen: resolved.open,
          openCut: resolved.openCut,
          loading: false,
        }
      })
      return created
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'No se pudo registrar el corte' })
      throw err
    }
  },
}))
